import React, { useState, useEffect, useRef } from 'react';
import {
  getAllEmds,
  getParticipatingTenders,
  getEmdForTender,
  createEmdForTender,
  updateEmdForTender,
  uploadEmdPdf,
  submitEmdForTender,
} from '../services/api';
import { formatDate } from '../utils/formatDate';
import { useToast } from '../context/ToastContext';
import PageHeader from './PageHeader';
import './TenderList.css';

const EMD_TYPES = [
  { value: 'dd', label: 'Demand Draft (DD)' },
  { value: 'bg', label: 'Bank Guarantee (BG)' },
  { value: 'online', label: 'Online Payment' },
  { value: 'fdr', label: 'Fixed Deposit Receipt (FDR)' },
];

const emptyForm = () => ({
  emd_type: '',
  emd_amount: '',
  beneficiary_name: '',
  validity_end_date: '',
  dd_number: '', dd_bank: '', dd_date: '',
  bg_number: '', bg_bank: '', bg_issue_date: '',
  transaction_ref: '', payment_date: '',
  fdr_number: '', fdr_bank: '', fdr_date: '',
});

function EmdList() {
  const { toast } = useToast();
  const [emds, setEmds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusTab, setStatusTab] = useState('');

  const [participatingTenders, setParticipatingTenders] = useState([]);
  const [selectedTenderId, setSelectedTenderId] = useState('');
  const [formEmd, setFormEmd] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formSubmitSuccess, setFormSubmitSuccess] = useState(null); // 'save' | 'submit' | null
  const formSuccessTimeoutRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedEmdId, setExpandedEmdId] = useState(null);
  const [showStagger, setShowStagger] = useState(false);
  const staggerShownRef = useRef(false);
  const [emdPdfFile, setEmdPdfFile] = useState(null);
  const [emdPdfUploading, setEmdPdfUploading] = useState(false);
  const [emdPdfError, setEmdPdfError] = useState(null);

  const fetchEmds = async () => {
    try {
      setLoading(true);
      const response = await getAllEmds(typeFilter ? { emd_type: typeFilter } : {});
      if (response.success) setEmds(response.emds || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  useEffect(() => {
    return () => { if (formSuccessTimeoutRef.current) clearTimeout(formSuccessTimeoutRef.current); };
  }, []);

  useEffect(() => {
    if (!loading && emds.length > 0 && !staggerShownRef.current) {
      staggerShownRef.current = true;
      setShowStagger(true);
    }
  }, [loading, emds.length]);

  useEffect(() => {
    if (!showStagger) return;
    const t = setTimeout(() => setShowStagger(false), 800);
    return () => clearTimeout(t);
  }, [showStagger]);

  const clearFilters = () => {
    setTypeFilter('');
    setStatusTab('');
  };

  const hasActiveFilters = typeFilter || statusTab;

  // When no EMDs exist, show the form so user can add one
  useEffect(() => {
    if (!loading && emds.length === 0) setShowForm(true);
  }, [loading, emds.length]);

  useEffect(() => {
    const fetchTenders = async () => {
      try {
        const res = await getParticipatingTenders({});
        if (res.success) setParticipatingTenders(res.tenders || []);
      } catch (e) {
        console.error('Failed to fetch participating tenders', e);
      }
    };
    fetchTenders();
  }, []);

  useEffect(() => {
    if (!selectedTenderId) {
      setFormEmd(null);
      setFormData(emptyForm());
      setFormLoading(false);
      return;
    }
    let cancelled = false;
    setFormError(null);
    setFormLoading(true);
    getEmdForTender(selectedTenderId)
      .then((res) => {
        if (cancelled || !res.success) return;
        const emd = res.emd;
        setFormEmd(emd);
        const tender = participatingTenders.find((t) => t.id === selectedTenderId);
        setFormData({
          emd_type: emd?.emd_type || '',
          emd_amount: emd?.emd_amount ?? tender?.emd_amount ?? '',
          beneficiary_name: emd?.beneficiary_name || '',
          validity_end_date: emd?.validity_end_date ? emd.validity_end_date.slice(0, 10) : '',
          dd_number: emd?.dd_number || '', dd_bank: emd?.dd_bank || '', dd_date: emd?.dd_date ? emd.dd_date.slice(0, 10) : '',
          bg_number: emd?.bg_number || '', bg_bank: emd?.bg_bank || '', bg_issue_date: emd?.bg_issue_date ? emd.bg_issue_date.slice(0, 10) : '',
          transaction_ref: emd?.transaction_ref || '', payment_date: emd?.payment_date ? emd.payment_date.slice(0, 10) : '',
          fdr_number: emd?.fdr_number || '', fdr_bank: emd?.fdr_bank || '', fdr_date: emd?.fdr_date ? emd.fdr_date.slice(0, 10) : '',
        });
      })
      .catch((err) => { if (!cancelled) setFormError(err.message); })
      .finally(() => { if (!cancelled) setFormLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTenderId, participatingTenders]);

  useEffect(() => {
    setEmdPdfError(null);
    setEmdPdfFile(null);
  }, [selectedTenderId, formEmd?.id]);

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const statusClass = (status) => {
    const map = {
      created: 'status-participating',
      submitted: 'status-submitted',
      locked: 'status-won',
      released: 'status-default',
      forfeited: 'status-lost',
      closed: 'status-default',
    };
    return map[status] || 'status-default';
  };

  const isExpired = (dateStr) => dateStr && new Date(dateStr) < new Date();
  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  const typeOptions = [
    { value: '', label: 'All types' },
    ...EMD_TYPES,
  ];

  const EMD_STATUS_TABS = [
    { value: '', label: 'All' },
    { value: 'created', label: 'Created' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'locked', label: 'Locked' },
    { value: 'released_forfeited', label: 'Released / Forfeited' },
    { value: 'closed', label: 'Closed' },
  ];

  const filterEmdsByStatus = (list) => {
    if (!statusTab) return list;
    if (statusTab === 'released_forfeited') return list.filter((e) => e.status === 'released' || e.status === 'forfeited');
    return list.filter((e) => e.status === statusTab);
  };

  const filteredEmds = filterEmdsByStatus(emds);

  const openEmdPdf = (tenderId, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    getEmdForTender(tenderId).then((res) => {
      if (res.success && res.emd?.pdf_url) window.open(res.emd.pdf_url, '_blank', 'noopener,noreferrer');
    }).catch(() => {});
  };

  const handleUploadEmdPdf = async () => {
    if (!selectedTenderId || !emdPdfFile) return;
    setEmdPdfError(null);
    setEmdPdfUploading(true);
    try {
      const res = await uploadEmdPdf(selectedTenderId, emdPdfFile);
      if (res.success && res.emd) {
        setFormEmd(res.emd);
        setEmdPdfFile(null);
        fetchEmds();
        toast.success('EMD PDF uploaded');
      }
    } catch (err) {
      setEmdPdfError(err?.message || 'Failed to upload PDF');
    } finally {
      setEmdPdfUploading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCreateEmd = async () => {
    if (!selectedTenderId) return;
    setFormError(null);
    setFormSaving(true);
    try {
      const res = await createEmdForTender(selectedTenderId);
      if (res.success) {
        setFormEmd(res.emd);
        fetchEmds();
      }
    } catch (err) {
      setFormError(err?.data?.error || err?.message || 'Failed to create EMD');
    } finally {
      setFormSaving(false);
    }
  };

  const handleSaveEmd = async () => {
    if (!selectedTenderId) return;
    setFormError(null);
    setFormSaving(true);
    try {
      const payload = {
        emd_type: formData.emd_type || undefined,
        emd_amount: formData.emd_amount !== '' ? Number(formData.emd_amount) : undefined,
        beneficiary_name: formData.beneficiary_name || undefined,
        validity_end_date: formData.validity_end_date || undefined,
        dd_number: formData.dd_number || undefined, dd_bank: formData.dd_bank || undefined, dd_date: formData.dd_date || undefined,
        bg_number: formData.bg_number || undefined, bg_bank: formData.bg_bank || undefined, bg_issue_date: formData.bg_issue_date || undefined,
        transaction_ref: formData.transaction_ref || undefined, payment_date: formData.payment_date || undefined,
        fdr_number: formData.fdr_number || undefined, fdr_bank: formData.fdr_bank || undefined, fdr_date: formData.fdr_date || undefined,
      };
      const res = await updateEmdForTender(selectedTenderId, payload);
      if (res.success) {
        setFormSaving(false);
        setFormSubmitSuccess('save');
        toast.success('EMD saved');
        setFormEmd(res.emd);
        fetchEmds();
        formSuccessTimeoutRef.current = setTimeout(() => setFormSubmitSuccess(null), 1200);
        return;
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleSubmitEmd = async () => {
    if (!selectedTenderId) return;
    setFormError(null);
    setFormSaving(true);
    try {
      const res = await submitEmdForTender(selectedTenderId);
      if (res.success) {
        setFormSaving(false);
        setFormSubmitSuccess('submit');
        toast.success('EMD submitted');
        setFormEmd(res.emd);
        fetchEmds();
        formSuccessTimeoutRef.current = setTimeout(() => setFormSubmitSuccess(null), 1200);
        return;
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const openNewForm = () => {
    setSelectedTenderId('');
    setFormData(emptyForm());
    setFormEmd(null);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (tenderId) => {
    setSelectedTenderId(tenderId);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedTenderId('');
    setFormEmd(null);
    setFormData(emptyForm());
    setFormError(null);
  };

  const getTenderLabel = (tenderId) => {
    const t = participatingTenders.find((x) => x.id === tenderId);
    return t ? `${t.tender_ref}${t.buyer_name ? ` – ${t.buyer_name}` : ''}` : tenderId;
  };

  return (
    <div className="tender-list-container">
      <PageHeader
        className="tender-list-header"
        title="Earnest Money Deposits (EMD)"
        subtitle="Manage EMDs from here: add new, edit, attach to participating tenders, and submit."
        actions={(
          <button type="button" className="confirm-go-ahead-btn" onClick={openNewForm}>
            + Add EMD
          </button>
        )}
      />

      {showForm && (
        <div
          className="emd-form-section"
          style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'var(--color-bg-subtle, #f9fafb)',
            borderRadius: '12px',
            border: '1px solid var(--color-border, #e5e7eb)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '1.1rem',
                color: 'var(--color-text, #1f2937)',
              }}
            >
              Setup or edit EMD
            </h3>
            <button type="button" className="cancel-btn" onClick={closeForm}>Close</button>
          </div>
          <div className="tender-details-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div className="detail-item">
              <label>Participating Tender</label>
              <select
                value={selectedTenderId}
                onChange={(e) => setSelectedTenderId(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
              >
                <option value="">Select a tender to attach EMD to...</option>
                {participatingTenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.tender_ref} {t.buyer_name ? `– ${t.buyer_name}` : ''} {t.emd_amount != null ? `(EMD ₹${Number(t.emd_amount).toLocaleString('en-IN')})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {formError && (
            <div className="error-message" style={{ marginBottom: '12px' }}>
              {formError}
              <button type="button" onClick={() => setFormError(null)}>×</button>
            </div>
          )}
          {formLoading && selectedTenderId && (
            <p style={{ color: 'var(--color-text-muted, #6b7280)', marginBottom: '12px' }}>Loading EMD...</p>
          )}
          {!formLoading && selectedTenderId && (
            <>
              {formEmd && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <span className={`status-badge ${statusClass(formEmd.status)}`}>{formEmd.status_display}</span>
                    {formEmd.emd_uid && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '13px',
                          color: 'var(--color-text-muted, #6b7280)',
                        }}
                      >
                        {formEmd.emd_uid}
                      </span>
                    )}
                  </div>
                  <div className="tender-details-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                <div className="detail-item">
                  <label>Type</label>
                  <select
                    value={formData.emd_type}
                    onChange={(e) => setFormData((f) => ({ ...f, emd_type: e.target.value }))}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value="">Select type</option>
                    {EMD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="detail-item">
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.emd_amount === '' ? '' : formData.emd_amount}
                    onChange={(e) => setFormData((f) => ({ ...f, emd_amount: e.target.value === '' ? '' : e.target.value }))}
                    placeholder={participatingTenders.find((t) => t.id === selectedTenderId)?.emd_amount}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div className="detail-item">
                  <label>Beneficiary</label>
                  <input
                    type="text"
                    value={formData.beneficiary_name}
                    onChange={(e) => setFormData((f) => ({ ...f, beneficiary_name: e.target.value }))}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div className="detail-item">
                  <label>Validity End</label>
                  <input
                    type="date"
                    value={formData.validity_end_date}
                    onChange={(e) => setFormData((f) => ({ ...f, validity_end_date: e.target.value }))}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                </div>
              </div>
              {formData.emd_type === 'dd' && (
                <div className="tender-details-grid" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                  <div className="detail-item"><label>DD Number</label><input type="text" value={formData.dd_number} onChange={(e) => setFormData((f) => ({ ...f, dd_number: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>DD Bank</label><input type="text" value={formData.dd_bank} onChange={(e) => setFormData((f) => ({ ...f, dd_bank: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>DD Date</label><input type="date" value={formData.dd_date} onChange={(e) => setFormData((f) => ({ ...f, dd_date: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                </div>
              )}
              {formData.emd_type === 'bg' && (
                <div className="tender-details-grid" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                  <div className="detail-item"><label>BG Number</label><input type="text" value={formData.bg_number} onChange={(e) => setFormData((f) => ({ ...f, bg_number: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>BG Bank</label><input type="text" value={formData.bg_bank} onChange={(e) => setFormData((f) => ({ ...f, bg_bank: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>BG Issue Date</label><input type="date" value={formData.bg_issue_date} onChange={(e) => setFormData((f) => ({ ...f, bg_issue_date: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                </div>
              )}
              {formData.emd_type === 'online' && (
                <div className="tender-details-grid" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                  <div className="detail-item"><label>Transaction Ref</label><input type="text" value={formData.transaction_ref} onChange={(e) => setFormData((f) => ({ ...f, transaction_ref: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>Payment Date</label><input type="date" value={formData.payment_date} onChange={(e) => setFormData((f) => ({ ...f, payment_date: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                </div>
              )}
              {formData.emd_type === 'fdr' && (
                <div className="tender-details-grid" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                  <div className="detail-item"><label>FDR Number</label><input type="text" value={formData.fdr_number} onChange={(e) => setFormData((f) => ({ ...f, fdr_number: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>FDR Bank</label><input type="text" value={formData.fdr_bank} onChange={(e) => setFormData((f) => ({ ...f, fdr_bank: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                  <div className="detail-item"><label>FDR Date</label><input type="date" value={formData.fdr_date} onChange={(e) => setFormData((f) => ({ ...f, fdr_date: e.target.value }))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }} /></div>
                </div>
              )}
              <div className="detail-item" style={{ marginTop: '16px' }}>
                <label>EMD document (PDF)</label>
                {formEmd.has_pdf || formEmd.pdf_url ? (
                  <p style={{ margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>PDF attached</span>
                    <a href={formEmd.pdf_url} target="_blank" rel="noopener noreferrer" className="confirm-go-ahead-btn" style={{ padding: '6px 12px', fontSize: '13px', textDecoration: 'none' }}>View PDF</a>
                  </p>
                ) : formEmd.status === 'created' && (
                  <>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => { setEmdPdfFile(e.target.files?.[0] || null); setEmdPdfError(null); }}
                      style={{ marginTop: '4px' }}
                    />
                    {emdPdfFile && (
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', color: 'var(--color-text-secondary, #6b7280)' }}>{emdPdfFile.name}</span>
                        <button type="button" className="confirm-go-ahead-btn" style={{ padding: '6px 12px' }} onClick={handleUploadEmdPdf} disabled={emdPdfUploading}>
                          {emdPdfUploading ? 'Uploading…' : 'Upload PDF'}
                        </button>
                      </div>
                    )}
                    {emdPdfError && <p style={{ margin: '4px 0 0 0', color: '#b91c1c', fontSize: '13px' }}>{emdPdfError}</p>}
                  </>
                )}
                {formEmd.status !== 'created' && !formEmd.has_pdf && !formEmd.pdf_url && (
                  <p style={{ margin: '4px 0 0 0', color: 'var(--color-text-secondary, #6b7280)', fontSize: '13px' }}>No PDF attached. PDF can only be added when EMD is in Created status.</p>
                )}
              </div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className={`confirm-go-ahead-btn ${formSaving ? 'btn-loading' : ''} ${formSubmitSuccess === 'save' ? 'btn-success-state' : ''}`} onClick={handleSaveEmd} disabled={formSaving || formSubmitSuccess}>
                  {formSubmitSuccess === 'save' ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden><polyline points="20 6 9 17 4 12"></polyline></svg>
                      Saved
                    </>
                  ) : formSaving ? 'Saving…' : 'Save EMD'}
                </button>
                {formEmd.status === 'created' && (
                  <button type="button" className={`edit-btn-large ${formSaving ? 'btn-loading' : ''} ${formSubmitSuccess === 'submit' ? 'btn-success-state' : ''}`} onClick={handleSubmitEmd} disabled={formSaving || formSubmitSuccess}>
                    {formSubmitSuccess === 'submit' ? (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden><polyline points="20 6 9 17 4 12"></polyline></svg>
                        Submitted
                      </>
                    ) : formSaving ? 'Submitting…' : 'Submit EMD'}
                  </button>
                )}
                <button type="button" className="cancel-btn" onClick={closeForm}>Close</button>
              </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="emd-status-tabs" style={{ marginBottom: '16px' }}>
        <div className="tabs-list" role="tablist">
          {EMD_STATUS_TABS.map((tab) => (
            <button
              key={tab.value || 'all'}
              type="button"
              role="tab"
              className={`tab-pill ${statusTab === tab.value ? 'active' : ''}`}
              onClick={() => setStatusTab(tab.value)}
            >
              {tab.label}
              <span className="tab-count">
                {tab.value === '' ? emds.length : tab.value === 'released_forfeited' ? emds.filter((e) => e.status === 'released' || e.status === 'forfeited').length : emds.filter((e) => e.status === tab.value).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-dropdowns">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {typeOptions.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="btn-secondary" onClick={clearFilters} style={{ marginLeft: 8 }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="tenders-table-wrapper">
          <table className="tenders-table table-skeleton">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>EMD Ref</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Beneficiary</th>
                <th>Validity End</th>
                <th>Status</th>
                <th>PDF</th>
                <th>Tender</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td><span className="skeleton-box" style={{ width: 20 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 120 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 88 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 60 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 60 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : emds.length === 0 ? (
        <div className="empty-state">
          <h3>No EMDs found</h3>
          <p>Use &quot;Add / Edit EMD&quot; to attach an EMD to a participating tender.</p>
          <button type="button" onClick={() => setShowForm(true)}>Add EMD</button>
        </div>
      ) : filteredEmds.length === 0 ? (
        <>
          <div className="empty-state">
            <h3>No EMDs in this status</h3>
            <p>Select another tab or add a new EMD.</p>
          </div>
          {emds.length > 0 && (
            <div className="list-footer">
              <span>Showing 0 of {emds.length} EMDs</span>
            </div>
          )}
        </>
      ) : (
        <div className="tenders-table-wrapper">
          <table className="tenders-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>EMD Ref</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Beneficiary</th>
                <th>Validity End</th>
                <th>Status</th>
                <th>PDF</th>
                <th>Tender</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={showStagger ? 'stagger-rows' : ''}>
              {filteredEmds.map((emd, index) => (
                <React.Fragment key={emd.id}>
                  <tr
                    style={showStagger ? { animationDelay: `${index * 0.03}s` } : undefined}
                    onClick={() => setExpandedEmdId(expandedEmdId === emd.id ? null : emd.id)}
                    className={`tender-row tender-row-clickable ${isExpired(emd.validity_end_date) ? 'deadline-passed' : ''} ${expandedEmdId === emd.id ? 'expanded' : ''}`}
                  >
                    <td className="expand-cell">
                      <svg className={`expand-icon ${expandedEmdId === emd.id ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </td>
                    <td><span className="ref-code">{emd.emd_uid || emd.id}</span></td>
                    <td>{emd.emd_type_display || emd.emd_type || '-'}</td>
                    <td>{formatCurrency(emd.emd_amount)}</td>
                    <td>{emd.beneficiary_name || '-'}</td>
                    <td>
                      <span className={isExpired(emd.validity_end_date) ? 'deadline passed' : isExpiringSoon(emd.validity_end_date) ? 'deadline approaching' : ''}>
                        {formatDate(emd.validity_end_date)}
                        {isExpired(emd.validity_end_date) && ' (Expired)'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${statusClass(emd.status)}`}>
                        {emd.status_display || emd.status}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {emd.has_pdf ? (
                        <button type="button" className="link-btn" onClick={(e) => openEmdPdf(emd.tender_id, e)}>View PDF</button>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary, #9ca3af)' }}>–</span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="tender-title-inline ref-code-link"
                        title="Edit EMD for this tender"
                        onClick={(e) => { e.stopPropagation(); openEditForm(emd.tender_id); }}
                      >
                        {getTenderLabel(emd.tender_id)}
                      </button>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => openEditForm(emd.tender_id)}
                        title="Edit EMD"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="link-btn"
                        style={{ marginLeft: '4px', fontSize: '12px' }}
                        onClick={() => openEditForm(emd.tender_id)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                  {expandedEmdId === emd.id && (
                    <tr className="tender-detail-row">
                      <td colSpan={10}>
                        <div className="tender-expanded-details">
                          <div className="tender-details-grid">
                            <div className="detail-item"><label>EMD Ref</label><span>{emd.emd_uid || emd.id}</span></div>
                            <div className="detail-item"><label>Type</label><span>{emd.emd_type_display || emd.emd_type || '-'}</span></div>
                            <div className="detail-item"><label>Amount</label><span>{formatCurrency(emd.emd_amount)}</span></div>
                            <div className="detail-item"><label>Beneficiary</label><span>{emd.beneficiary_name || '-'}</span></div>
                            <div className="detail-item"><label>Validity End</label><span>{formatDate(emd.validity_end_date)}</span></div>
                            <div className="detail-item"><label>Status</label><span className={`status-badge ${statusClass(emd.status)}`}>{emd.status_display || emd.status}</span></div>
                            <div className="detail-item"><label>Tender</label><span>{getTenderLabel(emd.tender_id)}</span></div>
                            {emd.has_pdf && (
                              <div className="detail-item">
                                <label>PDF</label>
                                <span><button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); openEmdPdf(emd.tender_id, e); }}>View PDF</button></span>
                              </div>
                            )}
                          </div>
                          {(emd.dd_number || emd.dd_bank) && (
                            <div className="tender-details-grid">
                              <div className="detail-item"><label>DD Number</label><span>{emd.dd_number || '-'}</span></div>
                              <div className="detail-item"><label>DD Bank</label><span>{emd.dd_bank || '-'}</span></div>
                              <div className="detail-item"><label>DD Date</label><span>{formatDate(emd.dd_date)}</span></div>
                            </div>
                          )}
                          {(emd.bg_number || emd.bg_bank) && (
                            <div className="tender-details-grid">
                              <div className="detail-item"><label>BG Number</label><span>{emd.bg_number || '-'}</span></div>
                              <div className="detail-item"><label>BG Bank</label><span>{emd.bg_bank || '-'}</span></div>
                              <div className="detail-item"><label>BG Issue Date</label><span>{formatDate(emd.bg_issue_date)}</span></div>
                            </div>
                          )}
                          {(emd.transaction_ref || emd.payment_date) && (
                            <div className="tender-details-grid">
                              <div className="detail-item"><label>Transaction Ref</label><span>{emd.transaction_ref || '-'}</span></div>
                              <div className="detail-item"><label>Payment Date</label><span>{formatDate(emd.payment_date)}</span></div>
                            </div>
                          )}
                          {(emd.fdr_number || emd.fdr_bank) && (
                            <div className="tender-details-grid">
                              <div className="detail-item"><label>FDR Number</label><span>{emd.fdr_number || '-'}</span></div>
                              <div className="detail-item"><label>FDR Bank</label><span>{emd.fdr_bank || '-'}</span></div>
                              <div className="detail-item"><label>FDR Date</label><span>{formatDate(emd.fdr_date)}</span></div>
                            </div>
                          )}
                          <button type="button" className="confirm-go-ahead-btn" style={{ marginTop: '12px' }} onClick={() => { openEditForm(emd.tender_id); setExpandedEmdId(null); }}>
                            Edit this EMD
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (emds.length > 0 || filteredEmds.length > 0) && (
        <div className="list-footer">
          <span>
            {statusTab ? `Showing ${filteredEmds.length} of ${emds.length} EMDs` : `Total: ${emds.length} EMDs`}
          </span>
        </div>
      )}
    </div>
  );
}

export default EmdList;
